import { useEffect, useRef } from 'react';
import useAuthStore from '../store/useAuthStore';
import useSubscriptionStore from '../store/useSubscriptionStore';
import { isNativeIOS } from '../services/iap';
import {
  fetchSubscriptionStatus, initIAP, promptUpgrade,
  startIapPurchase, makeGates, DEFAULT_LIMITS,
} from '../services/subscription';

export function SubscriptionProvider({ children }) {
  // Passthrough during migration — subscription state lives in Zustand, ops are module-level.
  return children;
}

// Bootstrap — fetch status on auth-ready, init IAP on iOS, retry on transient failure.
// Called ONCE at app root.
export function useSubscriptionBootstrap() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isConfigured = useAuthStore((s) => s.isConfigured);
  const clearSubscription = useSubscriptionStore((s) => s.clearSubscription);
  const fetchedRef = useRef(false);
  const iapInitRef = useRef(false);

  useEffect(() => {
    if (isAuthenticated && isConfigured && !fetchedRef.current) {
      fetchedRef.current = true;
      fetchSubscriptionStatus();
    }
    if (!isAuthenticated) {
      fetchedRef.current = false;
      // Clear cached subscription on sign-out so the next user doesn't briefly see stale Premium state.
      clearSubscription();
    }
  }, [isAuthenticated, isConfigured, clearSubscription]);

  useEffect(() => {
    if (isAuthenticated && isConfigured && isNativeIOS() && !iapInitRef.current) {
      iapInitRef.current = true;
      initIAP().then((result) => {
        if (!result.success && result.error !== 'not-ios') {
          setTimeout(() => initIAP(), 3000);
        }
      });
    }
  }, [isAuthenticated, isConfigured]);
}

export function useSubscription() {
  const subscriptionData = useSubscriptionStore((s) => s.subscriptionData);
  const showUpgradeModal = useSubscriptionStore((s) => s.showUpgradeModal);
  const setShowUpgradeModal = useSubscriptionStore((s) => s.setShowUpgradeModal);
  const upgradeContext = useSubscriptionStore((s) => s.upgradeContext);
  const purchasing = useSubscriptionStore((s) => s.purchasing);
  const purchaseError = useSubscriptionStore((s) => s.purchaseError);
  const setPurchaseError = useSubscriptionStore((s) => s.setPurchaseError);
  const iapReady = useSubscriptionStore((s) => s.iapReady);
  const iapError = useSubscriptionStore((s) => s.iapError);

  const tier = subscriptionData?.tier || 'free';
  const isPremium = tier === 'premium';
  const limits = subscriptionData?.limits || DEFAULT_LIMITS;
  const usage = subscriptionData?.usage || { recipeCount: 0, customCategoryCount: 0, cookingModeSessionsThisWeek: 0 };
  const gates = makeGates({ tier, limits, usage });

  return {
    tier,
    isPremium,
    limits,
    usage,
    subscriptionData,
    showUpgradeModal,
    setShowUpgradeModal,
    upgradeContext,
    purchasing,
    purchaseError,
    setPurchaseError,
    iapReady,
    iapError,
    retryIAP: initIAP,
    canAddRecipe: gates.canAddRecipe,
    canSaveFavorite: gates.canSaveFavorite,
    canAccessMealPlanWeek: gates.canAccessMealPlanWeek,
    canUseCookingMode: gates.canUseCookingMode,
    canAddCategory: gates.canAddCategory,
    getRecipeWarningLevel: gates.getRecipeWarningLevel,
    promptUpgrade,
    startIapPurchase,
    refreshSubscription: fetchSubscriptionStatus,
  };
}
